/**
 * Code generation for class groups, and the suffix parsing behind the console's
 * quick-add. The rule that makes the codes predictable: a group code ends with a
 * letter run (FORM-1-A, F1B, A), and each new group advances that run. Everything
 * before the run is the prefix and is never touched.
 */

const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

/** 0 -> "A", 25 -> "Z", 26 -> "AA", 27 -> "AB", 51 -> "AZ", 52 -> "BA". */
export function letterRunForIndex(index: number): string {
  let n = index + 1;
  let out = '';
  while (n > 0) {
    n -= 1;
    out = LETTERS[n % 26] + out;
    n = Math.floor(n / 26);
  }
  return out;
}

/** "A" -> "B", "Z" -> "AA", "AZ" -> "BA". */
export function nextLetterRun(run: string): string {
  const chars = run.split('');
  let i = chars.length - 1;
  while (i >= 0) {
    if (chars[i] === 'Z') {
      chars[i] = 'A';
      i -= 1;
    } else {
      chars[i] = LETTERS[LETTERS.indexOf(chars[i]) + 1];
      break;
    }
  }
  if (i < 0) return 'A'.repeat(chars.length + 1);
  return chars.join('');
}

/** "FORM-1-C" -> { prefix: "FORM-1-", run: "C" }. Null when the code has no trailing letters. */
export function splitSequence(code: string): { prefix: string; run: string } | null {
  const match = code.match(/^(.*?)([A-Z]+)$/);
  if (!match) return null;
  return { prefix: match[1], run: match[2] };
}

/** "Form 1" -> "FORM-1". Used only when the unit has no code to build on. */
export function slugForName(name: string): string {
  return name
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * The first seed for a unit's sequence: the last existing group's code, else the
 * unit's code, else a slug of the base name. Returns '' when nothing is usable.
 */
export function sequenceSeed(
  existingCodes: string[],
  unitCode: string,
  baseName: string,
): string {
  // Deliberately `||`, not `??`: an empty unit code must fall through to the name
  // slug, and `'' ?? x` short-circuits on empty strings.
  return existingCodes[existingCodes.length - 1] || unitCode || slugForName(baseName) || '';
}

/**
 * The next code after `seed`. Codes with a trailing letter run advance that run
 * (FORM-1-C -> FORM-1-D); codes without one gain a dash and a run (FORM-1 ->
 * FORM-1-A) so the sequence is always a sequence.
 */
export function nextGroupCodeAfter(seed: string): string {
  const parts = splitSequence(seed);
  if (parts) return `${parts.prefix}${nextLetterRun(parts.run)}`;
  return `${seed}-A`;
}

/**
 * "A, B, C" / "A B;C" / "4" -> ["A","B","C","D"]. A bare number means "that many
 * groups, continuing the alphabet" — the fastest input a busy administrator has.
 */
export function parseSuffixes(input: string): string[] {
  const trimmed = input.trim();
  if (!trimmed) return [];
  if (/^\d+$/.test(trimmed)) {
    const count = Math.min(parseInt(trimmed, 10), 100);
    return Array.from({ length: count }, (_, i) => letterRunForIndex(i));
  }
  return trimmed
    .split(/[\s,;]+/)
    .map((part) => part.trim())
    .filter(Boolean);
}

/** "Form 1" + "B" -> "Form 1 B". */
export function groupNameFor(baseName: string, suffix: string): string {
  return baseName.trim() ? `${baseName.trim()} ${suffix}` : suffix;
}